/**
 * PacketNotFound — Wave 3.10 defensive UI
 * --------------------------------------------------------------------------
 * Renders a friendly, actionable card when a packet detail page is opened
 * for a packet_id that no longer exists in the active packets collection.
 *
 * Behaviour matrix (driven by the /resolve endpoint response):
 *
 *   reason = 'reattributed'    → packet was converted/merged. Show
 *                                "Redirected to <new_packet_no>" with a
 *                                button to open the successor.
 *   reason = 'deleted_approval' → packet was an on-approval packet that
 *                                was returned/deleted. Show tombstone info.
 *   reason = 'missing'         → no trace found. Show generic message.
 *
 * Props:
 *   material        — 'diamond' | 'color_stone' | 'pearl' (route prefix)
 *   packetId        — the original (orphan) packet_id from the URL
 *   resolution      — the API response from /packets/{id}/resolve
 */
import React, { useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, ExternalLink, ArrowRight, Info } from 'lucide-react';

const ROUTE_PREFIX = {
  diamond: '/diamonds/packets',
  color_stone: '/color-stones/packets',
  pearl: '/pearls/packets',
};

const MATERIAL_LABEL = {
  diamond: 'Diamond',
  color_stone: 'Colour Stone',
  pearl: 'Pearl',
};

export default function PacketNotFound({
  material = 'diamond',
  packetId,
  resolution = null,
  autoRedirect = true,   // if reattributed, navigate to successor automatically
}) {
  const navigate = useNavigate();
  const prefix = ROUTE_PREFIX[material] || ROUTE_PREFIX.diamond;
  const reason = resolution?.reason || 'missing';

  // Auto-redirect for 'reattributed' (after a short delay so the user sees the message)
  useEffect(() => {
    if (
      autoRedirect &&
      reason === 'reattributed' &&
      resolution?.redirect_to_packet_id
    ) {
      const t = setTimeout(() => {
        navigate(`${prefix}/${resolution.redirect_to_packet_id}`, { replace: true });
      }, 1200);
      return () => clearTimeout(t);
    }
  }, [autoRedirect, reason, resolution, prefix, navigate]);

  return (
    <div className="py-8" data-testid={`packet-not-found-${material}`}>
      <Card className="max-w-2xl mx-auto p-6 border-amber-200 bg-amber-50/40">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-amber-100 p-2 shrink-0">
            <AlertTriangle size={20} className="text-amber-700" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold text-[var(--ink-navy)]">
                {MATERIAL_LABEL[material] || 'Packet'} not found
              </h2>
              <Badge variant="secondary" className="text-[10px] font-mono">
                {packetId?.slice(0, 8) || '—'}…
              </Badge>
            </div>

            {/* ============ Reattributed: graceful redirect ============ */}
            {reason === 'reattributed' && resolution?.redirect_to_packet_id && (
              <>
                <p className="text-sm text-slate-700 mt-2">
                  This packet was <strong>converted or merged</strong> into{' '}
                  <span className="font-mono font-medium text-[var(--ink-navy)]">
                    {resolution.redirect_to_packet_no || 'a new packet'}
                  </span>
                  . Redirecting you to the current packet…
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <Link
                    to={`${prefix}/${resolution.redirect_to_packet_id}`}
                    replace
                    data-testid="packet-not-found-redirect-link"
                  >
                    <Button size="sm" className="luxury-button">
                      <ArrowRight size={14} className="mr-1" />
                      Open {resolution.redirect_to_packet_no || 'successor'}
                    </Button>
                  </Link>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(-1)}
                    data-testid="packet-not-found-back-button"
                  >
                    <ArrowLeft size={14} className="mr-1" /> Back
                  </Button>
                </div>
              </>
            )}

            {/* ============ Deleted approval packet ============ */}
            {reason === 'deleted_approval' && (
              <>
                <p className="text-sm text-slate-700 mt-2">
                  This was an <strong>on-approval packet</strong> that has been
                  returned to the vendor and removed from inventory. The
                  historical record below is preserved for audit.
                </p>
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-slate-600 bg-white border border-amber-100 rounded p-3">
                  {resolution?.tombstone?.packet_no && (
                    <>
                      <span className="text-slate-500">Packet</span>
                      <span className="font-mono text-[var(--ink-navy)]">{resolution.tombstone.packet_no}</span>
                    </>
                  )}
                  {resolution?.tombstone?.classification_schema && (
                    <>
                      <span className="text-slate-500">Classification</span>
                      <span className="font-mono">{resolution.tombstone.classification_schema}</span>
                    </>
                  )}
                  {resolution?.tombstone?.reason && (
                    <>
                      <span className="text-slate-500">Reason</span>
                      <span>{resolution.tombstone.reason}</span>
                    </>
                  )}
                  {resolution?.tombstone?.deleted_at && (
                    <>
                      <span className="text-slate-500">Deleted at</span>
                      <span className="font-mono">{resolution.tombstone.deleted_at}</span>
                    </>
                  )}
                  {resolution?.tombstone?.deleted_by_email && (
                    <>
                      <span className="text-slate-500">By</span>
                      <span className="font-mono">{resolution.tombstone.deleted_by_email}</span>
                    </>
                  )}
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(-1)}
                    data-testid="packet-not-found-back-button"
                  >
                    <ArrowLeft size={14} className="mr-1" /> Back
                  </Button>
                </div>
              </>
            )}

            {/* ============ Generic missing (no trace) ============ */}
            {reason === 'missing' && (
              <>
                <p className="text-sm text-slate-700 mt-2">
                  This packet could not be located. It may have been deleted,
                  or the link you followed is from an older version of the
                  system before re-attribution metadata was tracked.
                </p>
                <div className="mt-3 rounded p-3 bg-white border border-amber-100 text-xs text-slate-600 flex items-start gap-2">
                  <Info size={14} className="mt-0.5 text-amber-600 shrink-0" />
                  <span>
                    Try the {MATERIAL_LABEL[material]} list page — the current
                    packet should still be discoverable there with a similar
                    packet number.
                  </span>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(-1)}
                    data-testid="packet-not-found-back-button"
                  >
                    <ArrowLeft size={14} className="mr-1" /> Back
                  </Button>
                  <Link to={prefix.replace('/packets', '')} data-testid="packet-not-found-list-link">
                    <Button size="sm" variant="outline">
                      <ExternalLink size={14} className="mr-1" />
                      Open {MATERIAL_LABEL[material]}s list
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
